/// <reference types="w3c-web-usb" />

declare global {
  interface USBDevice extends EventTarget {}
}

function getEndpointNumbers(device: USBDevice): { in: number | null; out: number | null } {
  const config = device.configuration;
  if (!config) return { in: null, out: null };
  const iface = config.interfaces[0];
  const altSetting = iface.alternate;

  let endpointIn: number | null = null;
  let endpointOut: number | null = null;

  for (const ep of altSetting.endpoints) {
    if (ep.type === 'bulk') {
      if (ep.direction === 'in' && endpointIn === null) {
        endpointIn = ep.endpointNumber;
      } else if (ep.direction === 'out' && endpointOut === null) {
        endpointOut = ep.endpointNumber;
      }
    }
  }

  return { in: endpointIn, out: endpointOut };
}

export async function sendSerialData(device: USBDevice, endpointOut: number, data: string): Promise<void> {
  const encoded = new TextEncoder().encode(data + '\n');
  await Promise.race([
    device.transferOut(endpointOut, encoded),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`transferOut(endpoint ${endpointOut}) timed out after 5s`)), 5000),
    ),
  ]);
}

export async function receiveSerialData(device: USBDevice, endpointIn: number): Promise<string> {
  const result = await Promise.race([
    device.transferIn(endpointIn, 256),
    new Promise<USBInTransferResult>((_, reject) =>
      setTimeout(() => reject(new Error(`transferIn(endpoint ${endpointIn}) timed out after 5s`)), 5000),
    ),
  ]) as USBInTransferResult;
  if (!result.data) return '';
  return new TextDecoder().decode(result.data);
}

export function getDataEndpoint(device: USBDevice): number | null {
  const endpoints = getEndpointNumbers(device);
  return endpoints.out;
}

export function getInEndpoint(device: USBDevice): number | null {
  const endpoints = getEndpointNumbers(device);
  return endpoints.in;
}

/**
 * Send a command and read the full response (equivalent to Python's cli.execute).
 * Reads all available lines until no more data arrives within the timeout.
 */
export async function executeCommand(
  device: USBDevice,
  endpointOut: number,
  endpointIn: number,
  cmd: string,
  responseTimeoutMs = 2000,
): Promise<string> {
  // Send command
  const encoded = new TextEncoder().encode(cmd + '\n');
  await Promise.race([
    device.transferOut(endpointOut, encoded),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('transferOut timed out after 5s')), 5000),
    ),
  ]);

  // Read all response lines
  let result = '';
  const deadline = Date.now() + responseTimeoutMs;

  while (Date.now() < deadline) {
    try {
      const readResult = await Promise.race([
        device.transferIn(endpointIn, 1024),
        new Promise<USBInTransferResult>((_, reject) =>
          setTimeout(() => reject(new Error('read timeout')), 500),
        ),
      ]) as USBInTransferResult;

      if (!readResult.data || readResult.data.byteLength === 0) {
        break; // No more data
      }

      const text = new TextDecoder().decode(readResult.data);
      result += text;
      console.log('[USB-Serial RX]', text);
    } catch {
      // Timeout — no more data available
      break;
    }
  }

  return result.replace(/\r/g, '');
}

export async function sendConfigToDevice(device: USBDevice, commands: string[]): Promise<boolean> {
  const endpoints = getEndpointNumbers(device);

  if (endpoints.out === null) {
    return false;
  }

  for (const cmd of commands) {
    if (cmd.startsWith('#')) continue;
    await sendSerialData(device, endpoints.out, cmd);
  }

  return true;
}
